import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import SEO from '../SEO.js';

// On mobile the lightbox overlay scrolls (see the flex-col/overflow-y-auto
// wrapper below), so a large slideHeight (e.g. 82vh) leaves little room for
// the caption below without a lot of scrolling — cap it there.
const isMobileLightbox = typeof window !== 'undefined' && window.innerWidth <= 767;

const pieces = [
    {
        id: 'blackfest',
        src: require('../photos/blackfest_poster.jpg'),
        alt: 'Stanford Blackfest Poster',
        title: 'Stanford Blackfest Poster',
        year: '2026',
        description: "Blackfest is Stanford's annual music festival celebrating Black culture and featuring prominent artists. Alongside a team of 3 designers, I collaborated on the graphics and creative direction for Blackfest 2026.\nJID personally said the poster was fire.",
        slides: [
            require('../photos/blackfest_poster.jpg'),
            require('../photos/blackfest_event.jpg'),
            require('../photos/blackfest_orange_black.png'),
            require('../photos/blackfest_blue_pink.png'),
        ],
    },
    {
        id: 'common_thread',
        src: require('../photos/common_thread.jpg'),
        alt: 'Common Thread',
        title: 'common thread',
        year: '2026',
        subtitle: 'Stanford event',
        description: "Designed the poster for an MS Design student's thesis presentation centering creative making and fabric upcycling.",
        slides: [
            require('../photos/common_thread.jpg'),
            require('../photos/common_thread_event.jpg'),
        ],
    },
    {
        id: 'timepiece',
        src: require('../photos/timepiece3.mp4'),
        previewType: 'video',
        poster: require('../photos/dreamtime_first_still.png'),
        alt: 'Timepiece',
        title: 'dreamtime',
        year: '2025',
        subtitle: 'exhibited at Stanford d.school',
        slideHeight: '82vh',
        descriptionWidth: 'md:w-96',
        description: "How do we experience time in dreams?\nI've been enthralled by the unexplainable nature of dreams since childhood—how is it possible to experience complex events in the matter of seconds while dreaming? Based on my REM cycles, I display eight hours of sleep, with a correlated visual projection-mapped onto a hanging piece of cloth. The eight visuals depict the flow of time in each hour, accompanied by a surrounding red ring that signals how fast dreamtime is moving.",
        slides: [
            { type: 'video', src: require('../photos/timepiece3.mp4') },
            { type: 'video', src: '/photos/dreamtime.mp4' },
            { type: 'video', src: '/photos/dreamtime2.mp4' },
            { type: 'image', src: require('../photos/dreamtime_still.png') },
        ],
    },
    {
        id: 'alter_ego',
        src: require('../photos/alter_ego.jpeg'),
        alt: 'Alter Ego',
        title: 'alter ego',
        year: '2022',
        subtitle: 'charcoal, exhibited at Columbia',
    },
    {
        id: 'deepest_corner',
        src: require('../photos/deepest_corner.png'),
        alt: 'Deepest Corner',
        title: 'ether',
        year: '2022',
        subtitle: 'ink',
    },
];

const Art = () => {
    const [lightbox, setLightbox] = useState(null);
    const [slideIndex, setSlideIndex] = useState(0);
    const [slideMaxWidth, setSlideMaxWidth] = useState(null);
    const [hoverSide, setHoverSide] = useState(null);
    const slideWrapperRef = React.useRef(null);

    const openLightbox = (piece) => {
        setSlideIndex(0);
        setSlideMaxWidth(null);
        setLightbox(piece);
    };

    const closeLightbox = () => setLightbox(null);

    useEffect(() => {
        if (!lightbox?.slides) return;
        const hVh = parseFloat(lightbox.slideHeight || '70');
        const hPx = ((isMobileLightbox ? Math.min(hVh, 48) : hVh) / 100) * window.innerHeight;
        const imageSrcs = lightbox.slides
            .filter(s => !s.type || s.type === 'image')
            .map(s => s.src ?? s);
        if (imageSrcs.length === 0) return;
        let maxW = 0;
        let remaining = imageSrcs.length;
        imageSrcs.forEach(src => {
            const img = new window.Image();
            img.onload = () => {
                const w = (img.naturalWidth / img.naturalHeight) * hPx;
                if (w > maxW) maxW = w;
                if (--remaining === 0) setSlideMaxWidth(maxW);
            };
            img.onerror = () => { if (--remaining === 0 && maxW > 0) setSlideMaxWidth(maxW); };
            img.src = src;
        });
    }, [lightbox]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') closeLightbox(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return (
        <>
            <div className='grid'>
                <SEO
                    title="Art"
                    description="Art and design work by Lord Crawford — posters, creative direction, and visual craft, including Stanford Blackfest and other collaborations."
                    path="/art"
                />
                <div className="text-[4vw] ml-[3.5%] mt-[2%] space-y-[-2vw] invisible title-spacer" aria-hidden="true"><div>Lord</div><div>Crawford</div></div>

                <div id="images" className="ml-[2%] mr-[2%] md:ml-[3.5%] md:mr-[3.5%] mt-[4%] mb-10 flex gap-4 md:gap-10">
                    {[[pieces[0], pieces[3]], [pieces[2], pieces[1], pieces[4]]].map((col, ci) => (
                        <div key={ci} className="flex-1 flex flex-col gap-6 md:gap-16">
                            {col.map((piece) => (
                                <div
                                    key={piece.id}
                                    className="cursor-pointer group"
                                    onClick={() => openLightbox(piece)}
                                >
                                    <div
                                        className="overflow-hidden"
                                        style={piece.id === 'common_thread' ? {boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'visible'} : undefined}
                                    >
                                        {piece.previewType === 'video' ? (
                                            <video
                                                className="w-full h-auto block transition-opacity duration-300 group-hover:opacity-80 pointer-events-none"
                                                src={piece.src}
                                                poster={piece.poster}
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                            />
                                        ) : (
                                            <img
                                                className="w-full h-auto block transition-opacity duration-300 group-hover:opacity-80 pointer-events-none"
                                                src={piece.src}
                                                alt={piece.alt}
                                            />
                                        )}
                                    </div>
                                    <div className="mt-2 text-sm md:text-[1vw] font-raleway text-stone-600">
                                        <span className="font-[600] text-black">{piece.title}</span>
                                        <span className="font-[400]">, {piece.year}</span>
                                        {piece.subtitle && <span className="font-[400] text-stone-400"> — {piece.subtitle}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {lightbox && ReactDOM.createPortal(
                <div
                    className="fixed inset-0 z-[9999] flex flex-col items-center pt-24 pb-16 overflow-y-auto md:flex-row md:items-center md:justify-center md:pt-0 md:pb-0"
                    style={{backgroundColor: 'rgba(0,0,0,0.88)'}}
                    onClick={closeLightbox}
                >
                    <button
                        className="fixed top-8 left-8 flex items-center gap-2 text-white/70 hover:text-white text-2xl leading-none z-10 px-2 py-1 font-raleway font-light transition-colors"
                        onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                    >
                        <span className="text-[1.6em] leading-none -mt-0.5">‹</span> back
                    </button>

                    <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-16 w-full max-w-7xl px-4 md:px-10 md:ml-[-4vw]">

                        {/* Slideshow column */}
                        <div className="md:flex-1 min-w-0 flex flex-col items-center">
                            {/* Clicking anywhere here navigates prev/next based on x position */}
                            <div
                                className="relative w-full flex items-center justify-center"
                                style={{height: isMobileLightbox ? `min(${lightbox.slideHeight || '70vh'}, 48vh)` : (lightbox.slideHeight || '70vh')}}
                                onClick={(e) => {
                                    if (!lightbox.slides || lightbox.slides.length <= 1) return;
                                    const wrapper = slideWrapperRef.current;
                                    if (wrapper) {
                                        const r = wrapper.getBoundingClientRect();
                                        if (e.clientX < r.left || e.clientX > r.right) return;
                                    }
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    if (e.clientX < rect.left + rect.width / 2) {
                                        if (slideIndex > 0) setSlideIndex(i => i - 1);
                                    } else {
                                        if (slideIndex < lightbox.slides.length - 1) setSlideIndex(i => i + 1);
                                    }
                                }}
                            >
                                {/* fit-content wrapper sized to widest image — arrows sit just outside it */}
                                <div ref={slideWrapperRef} className="relative flex items-center justify-center" style={{width: slideMaxWidth ? `${slideMaxWidth}px` : 'fit-content', maxWidth: 'calc(100% - 5rem)'}}>
                                    {(() => {
                                        const maxH = lightbox.slideHeight || '70vh';
                                        if (!lightbox.slides) {
                                            return <img style={{maxHeight: maxH, height: 'auto', width: 'auto', maxWidth: '100%', display: 'block'}} src={lightbox.src} alt={lightbox.alt} />;
                                        }
                                        const slide = lightbox.slides[slideIndex];
                                        const isVideo = slide?.type === 'video';
                                        const src = slide?.src ?? slide;
                                        return isVideo ? (
                                            <video key={src} style={{maxHeight: maxH, height: 'auto', width: 'auto', maxWidth: '100%', display: 'block'}} onClick={(e) => e.stopPropagation()} src={src} autoPlay loop muted playsInline />
                                        ) : (
                                            <img style={{maxHeight: maxH, height: 'auto', width: 'auto', maxWidth: '100%', display: 'block'}} src={src} alt={lightbox.alt} />
                                        );
                                    })()}
                                    {lightbox.slides && lightbox.slides.length > 1 && (
                                        <>
                                            {/* Transparent click zones — hover state drives arrow highlight */}
                                            <div
                                                className={`absolute inset-y-0 left-0 w-1/2 z-10 ${slideIndex > 0 ? 'cursor-pointer' : 'cursor-default'}`}
                                                onMouseEnter={() => slideIndex > 0 && setHoverSide('left')}
                                                onMouseLeave={() => setHoverSide(null)}
                                            />
                                            <div
                                                className={`absolute inset-y-0 right-0 w-1/2 z-10 ${slideIndex < lightbox.slides.length - 1 ? 'cursor-pointer' : 'cursor-default'}`}
                                                onMouseEnter={() => slideIndex < lightbox.slides.length - 1 && setHoverSide('right')}
                                                onMouseLeave={() => setHoverSide(null)}
                                            />
                                            <button
                                                className={`absolute z-20 text-white text-5xl leading-none select-none transition-all duration-200 px-3 py-6 ${slideIndex === 0 ? 'opacity-0 pointer-events-none' : hoverSide === 'left' ? 'opacity-100 scale-110' : 'opacity-40 hover:opacity-100 hover:scale-110'}`}
                                                style={{right: 'calc(100% + 0.6rem)', top: '50%', transform: 'translateY(-50%)'}}
                                                onMouseEnter={() => setHoverSide('left')}
                                                onMouseLeave={() => setHoverSide(null)}
                                                onClick={(e) => { e.stopPropagation(); setSlideIndex(i => i - 1); }}
                                            >‹</button>
                                            <button
                                                className={`absolute z-20 text-white text-5xl leading-none select-none transition-all duration-200 px-3 py-6 ${slideIndex === lightbox.slides.length - 1 ? 'opacity-0 pointer-events-none' : hoverSide === 'right' ? 'opacity-100 scale-110' : 'opacity-40 hover:opacity-100 hover:scale-110'}`}
                                                style={{left: 'calc(100% + 0.6rem)', top: '50%', transform: 'translateY(-50%)'}}
                                                onMouseEnter={() => setHoverSide('right')}
                                                onMouseLeave={() => setHoverSide(null)}
                                                onClick={(e) => { e.stopPropagation(); setSlideIndex(i => i + 1); }}
                                            >›</button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Dots — always below the image, never overlapping it */}
                            {lightbox.slides && lightbox.slides.length > 1 && (
                                <div className="mt-3 flex flex-col items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex gap-2 items-center">
                                        {lightbox.slides.map((_, i) => (
                                            <button
                                                key={i}
                                                onClick={(e) => { e.stopPropagation(); setSlideIndex(i); }}
                                                className="rounded-full transition-all duration-200 cursor-pointer"
                                                style={i === slideIndex
                                                    ? {width: 8, height: 8, background: 'white'}
                                                    : {width: 6, height: 6, border: '1.5px solid rgba(255,255,255,0.55)', background: 'transparent'}
                                                }
                                            />
                                        ))}
                                    </div>
                                    <span className="text-white/40 text-xs font-raleway tracking-widest">{slideIndex + 1} / {lightbox.slides.length}</span>
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        <div className={`md:flex-none md:self-center text-white w-full ${lightbox.descriptionWidth ? lightbox.descriptionWidth : 'md:w-72'}`} onClick={(e) => e.stopPropagation()}>
                            <div className="text-[15px] font-raleway">
                                <span className="font-[600]">{lightbox.title}</span>
                                <span className="font-[400]">, {lightbox.year}</span>
                                {lightbox.subtitle && <span className="text-white/50"> — {lightbox.subtitle}</span>}
                            </div>
                            {lightbox.description && (
                                <div className="mt-3 text-[15px] leading-relaxed text-white/65 font-raleway">
                                    {lightbox.description.split('\n').map((line, i) => (
                                        <p key={i} className={i > 0 ? "mt-2" : ""}>{line}</p>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>
                </div>,
                document.body
            )}
        </>
    )
}

export default Art
